import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ProductSize } from "../models/ProductSize";
import {
	duplicateProductSize,
	removeProductSize,
	upsertProductSize,
} from "../repositories/ProductSizesRepository";
import { PRODUCT_SIZES_QUERY_KEYS } from "./useProductSizes";

interface MutationOptions {
	onSuccess?: (data?: ProductSize) => void;
	onError?: (error: Error) => void;
}

export const useUpsertProductSize = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (product: ProductSize) => {
			return await upsertProductSize(product);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: PRODUCT_SIZES_QUERY_KEYS.all,
			});
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertProductSize: mutation.mutate,
		upsertProductSizeAsync: mutation.mutateAsync,
		isUpserting: mutation.isPending,
	};
};

export const useDuplicateProductSize = ({
	onSuccess,
	onError,
}: {
	onSuccess?: (product: ProductSize) => void;
	onError?: (error: Error) => void;
} = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (input: {
			id: number;
			name: string;
			sectionCode: string;
		}) => await duplicateProductSize(input),
		onSuccess: (product) => {
			queryClient.invalidateQueries({
				queryKey: PRODUCT_SIZES_QUERY_KEYS.all,
			});
			onSuccess?.(product);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		duplicateProductSize: mutation.mutate,
		isDuplicating: mutation.isPending,
	};
};

export const useDeleteProductSize = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (id: number) => {
			await removeProductSize(id);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: PRODUCT_SIZES_QUERY_KEYS.all,
			});
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		deleteProductSize: mutation.mutate,
		deleteProductSizeAsync: mutation.mutateAsync,
		isDeleting: mutation.isPending,
	};
};
